import { extractUserTurn, extractAssistantTurn } from 'features/agent-analytics/utils/conversationTurns';

/**
 * Search within one conversation's transcript.
 *
 * Deliberately client-side and exhaustive rather than a call to the semantic
 * `/agent-runs/search` endpoint. Two reasons:
 *
 *  - Scope. That endpoint ranks the top N runs across the whole team; its
 *    modular filters do not constrain by conversation_id (verified: a
 *    conversation-scoped query came back with runs from 19 other
 *    conversations). Intersecting a global top-N with this conversation would
 *    silently miss matches that didn't rank globally.
 *  - Recall. Embedding rank is what you want when choosing among thousands of
 *    runs. Inside one conversation of a handful of exchanges, matching every
 *    occurrence beats ranking a few — the drawer already holds the full text,
 *    so there is nothing to approximate.
 *
 * Matching is case-insensitive and AND-across-terms, over the rendered turn
 * text plus the raw payloads, so a query hits content the transcript folded
 * away (system prompts, tool payloads) as well as what's on screen.
 */

function payloadText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

/** All searchable text for one run, lowercased once. */
function haystack(run) {
  const user = extractUserTurn(run.input);
  const agent = extractAssistantTurn(run.output);
  return [
    user?.text,
    agent?.text,
    payloadText(user?.value),
    payloadText(agent?.value),
    payloadText(run.input),
    payloadText(run.output),
    run.error_summary,
    run.error_type,
    run.agent_name,
  ]
    .filter(Boolean)
    .join('   ')
    .toLowerCase();
}

/** Split a query into terms, honouring "quoted phrases". */
export function parseQuery(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  const terms = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m;
  while ((m = re.exec(q)) !== null) terms.push(m[1] || m[2]);
  return terms.filter(Boolean);
}

/**
 * @returns {{ runs: Array, terms: string[], matchedCount: number }}
 * `runs` is the original list when the query is empty, else only matches.
 */
export function searchConversation(runs, query) {
  const terms = parseQuery(query);
  if (!terms.length) return { runs, terms: [], matchedCount: runs.length };
  const matched = runs.filter((run) => {
    const hay = haystack(run);
    return terms.every((t) => hay.includes(t));
  });
  return { runs: matched, terms, matchedCount: matched.length };
}
